import moment from 'moment';
import _ from 'lodash';

/**
 * Holds the user's current cart. The cart object structure matches the one needed to validate and place an
 * order on the Online Ordering API.
 *
 * The cart items objects in the cart are simply 'decorators' of the full item/modifier hierarchy stored in modifierDialogController.
 * They only contain selected objects and the fields needed for our API.
 * When editing a cart item, we use this lightweight structure to decorate the full hierarchy with the chosen selections.
 *
 * Injections:
 *   $http: for adding and removing cart items on our server
 */
export function cartService(
    $http,
    $q,
    FULFILLMENT_TYPES,
    LOCAL_STORAGE,
    ORDER_TYPES,
    OUT_OF_STOCK_ERROR,
    ToastApiService,
    blockingCalloutService,
    calloutService,
    cartStatusChannel,
    featureFlagService,
    itemDetailsService,
    localStorageService,
    restaurantHoursService,
    restaurantInfoFactory
) {
    'ngInject';
    cartService.data = {}; // Public data
    let _data = {}; // Private data
    let completedCarts = [];
    let isCartUpdated = false;
    let cartPromise = null;

    return {
        data: cartService.data,
        getCart: getCart,
        addCartItem: addCartItem,
        removeCartItem: removeCartItem,
        updateCart: updateCart,
        configureCart: configureCart,
        clearCart: clearCart,
        getPreviousCart: getPreviousCart,
        setCartError: setCartError,
        clearCartError: clearCartError,
        getCartData: getCartData,
        clearCartGuidAsync,
        getCartUrl,
        hasWarnings: hasWarnings,
        resyncCart,
        setCart,
        modifyCartErrorHandler,

        //exported for testing
        _getCartGuidAsync,
        _getTrimmedItem,
        _notifyChanges
    };

    /**
      * getCart(): returns a promise
      *  -> either loads the cart from our server or returns the already loaded cart
      */
    function getCart() {
        const cart = cartService.data.cart;
        if (_isSet(cart)) {
            return $q.resolve(cart);
        } else {
            return _cartPromise().then(
                () => $q.resolve(cartService.data.cart),
                error => $q.reject(error)
            );
        }
    }

    /**
     * For enlightenment, G2 doesn't create a new cart automatically like toastweb did so
     * we need to do so ourselves.
     *
     * @returns {Promise<void>} resolves if we already have a cartGuid or once a new cart
     *  has been created
     */
    function _createCartIfAbsent() {
        // After the order is placed, we wipe out the old cart but call resyncCart() in order to
        // configure the new empty cart with the same fulfillment info as the previous cart.
        // Therefore we may actually need to create the new cart at this point now that
        // we're out of toastweb.

        return _getCartGuidAsync().then(guid => {
            if (guid) {
                return $q.resolve();
            }

            // The intent is to resolve without a return value in all cases.
            return getCart().then(() => $q.resolve());
        });
    }

    function _isSet(obj) {
        return angular.isDefined(obj) && !angular.equals({}, obj);
    }

    function _cartPromise() {
        if (cartPromise === null) {
            const promise =  _cartPromiseEnlightened();

            cartPromise = promise
                .then(response => {
                    setCart(response.data);
                    clearCartError();
                }).finally(() => {
                    cartPromise = null;
                });
        }

        return cartPromise;
    }

    /**
     * Retrieves the last known cart guid. It is possible to have a cart guid but to
     * not yet have loaded the cart from the server (in case we have a guid in local
     * storage).
     *
     * @returns {Promise<string>} resolves with the cart guid, may resolve with `null`
     */
    function _getCartGuidAsync() {
        if (_data.cartGuid === undefined) {
            return restaurantInfoFactory.getInfo(['general'])
                .then(info => info.general.guid)
                .then(restaurantGuid => {
                    // TODO extract a cartGuidStorageService.
                    const cartGuidMap = new Map(JSON.parse(localStorageService.get(LOCAL_STORAGE.CART_GUID_MAP)));
                    _data.cartGuid = cartGuidMap.get(restaurantGuid) || null;
                    return _data.cartGuid;
                });
        }

        return $q.resolve(_data.cartGuid);
    }

    function _setCartGuid(restaurantGuid, cartGuid) {
        // TODO extract a cartGuidStorageService.
        const cartGuidMap = new Map(JSON.parse(localStorageService.get(LOCAL_STORAGE.CART_GUID_MAP)));

        if (cartGuid) {
            cartGuidMap.set(restaurantGuid, cartGuid);
        } else {
            cartGuidMap.delete(restaurantGuid);
        }

        localStorageService.set(LOCAL_STORAGE.CART_GUID_MAP, JSON.stringify(Array.from(cartGuidMap.entries())));
        _data.cartGuid = cartGuid;
    }

    /**
     * @returns {Promise<void>}
     */
    function clearCartGuidAsync() {
        return restaurantInfoFactory.getInfo(['general'])
            .then(info => info.general.guid)
            .then(restaurantGuid => _setCartGuid(restaurantGuid, null));
    }

    /**
     * Returns the base cart URL for cart requests to G2.
     *
     * @param {string} cartGuid defaults to the current cart's guid
     * @returns {string} base cart URL
     */
    function getCartUrl(cartGuid = _data.cartGuid) {
        let url = `${ToastApiService.data.url}/online-ordering/v1/carts/enlightened`;

        if (cartGuid) {
            url += `/${cartGuid}`;
        }

        return url;
    }

    function _getCartItemUrl(cartGuid, itemExternalId) {
        let url = getCartUrl(cartGuid) + '/items';

        if (itemExternalId) {
            url += `/${itemExternalId}`;
        }

        return url;
    }

    function _cartPromiseEnlightened() {
        const createCartFn = () => restaurantInfoFactory
            .getInfo(['general'])
            .then(info => info.general.guid)
            .then(restaurantGuid => $http({
                url: getCartUrl(null),
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                transformRequest: data => {
                    // angular can't encode form params by itself.
                    let fields = [];
                    for (var field in data) {
                        fields.push(encodeURIComponent(field) + '=' + encodeURIComponent(data[field]));
                    }
                    return fields.join('&');
                },
                data: { restaurantGuid }
            }));

        return _getCartGuidAsync().then(cartGuid => {
            if (cartGuid) {
                return $http({
                    url: getCartUrl(cartGuid),
                    method: 'GET',
                    skipCallout: true
                }).catch(errResp => {
                    // We had a stale cart guid.
                    if (404 === errResp.status) {
                        return createCartFn();
                    }

                    return $q.reject(errResp);
                });
            }

            return createCartFn();
        });
    }

    function setCartError(error) {
        cartService.data.error = error;
    }

    function clearCartError() {
        cartService.data.error = null;
    }

    function hasWarnings() {
        return cartService.data && cartService.data.cart && cartService.data.cart.warnings && cartService.data.cart.warnings.length;
    }

    /**
      * getCartData(): returns the client copy of the cart from the last time cart was retrieved from the server
      */
    function getCartData() {
        return cartService.data;
    }

    function resyncCart() {
        let diningOption = cartService.data.cart.diningOptionBehavior;
        let fulfillmentType = cartService.data.cart.fulfillmentType;
        let fulfillmentMoment = FULFILLMENT_TYPES.FUTURE === fulfillmentType ? moment.parseZone(cartService.data.cart.fulfillmentDateTime) : null;

        clearCart();

        return configureCart(diningOption, fulfillmentType, fulfillmentMoment);
    }

    /**
      * configureCart(): returns a promise
      *  -> configures the dining option behaviour and fulfillment type of the cart
      */
    function configureCart(diningOptionBehavior, fulfillmentType, fulfillmentMoment, isCheckout) {
        var deferred = $q.defer(),
            fulfillmentDate;

        switch (fulfillmentType) {
        case FULFILLMENT_TYPES.FUTURE:
            fulfillmentDate = fulfillmentMoment.format();
            break;
        case FULFILLMENT_TYPES.ASAP:
            break;
        default:
            var errorMessage = 'Invalid Date';
            calloutService.error(errorMessage);
            deferred.reject(errorMessage);
            return deferred.promise;
        }

        let configureCartPromise;
        let errorHandler;
        const requestData = {
            diningOptionBehavior: diningOptionBehavior,
            fulfillmentType: fulfillmentType,
            fulfillmentDate: fulfillmentDate,
            isCheckout: isCheckout
        };

        configureCartPromise = restaurantInfoFactory
            .getInfo(['general'])
            .then(info => {
                // The JS basically treats dates/time as local values, ignoring the timezone/offset.
                // We need to set the correct timezone offset before sending to the server by
                // stripping out whatever offset was passed in and re-parsing it in the restaurant's
                // timezone.
                if (fulfillmentDate) {
                    fulfillmentDate = moment.tz(
                        fulfillmentMoment.format('YYYY-MM-DD HH:mm'),
                        info.general.timeZone
                    ).format();
                    requestData.fulfillmentDate = fulfillmentDate;
                }
            })
            .then(_createCartIfAbsent)
            .then(() => $http({
                url: `${getCartUrl()}/fulfillment`,
                method: 'PATCH',
                dataType: 'JSON',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: requestData,
                skipCallout: true
            }));
        errorHandler = errResp => modifyCartErrorHandler({ errResp });

        configureCartPromise.then(function(response) {
            let cart = cartService.data.cart;
            /**
              * Temporarily hold check information because server cart check is transient
              * This is a temporary fix
              */
            var tempCheck = null;
            if (cart && cart.check) {
                tempCheck = cart.check;
            }

            cart = response.data;
            cart.check = tempCheck;

            let fulfillmentMomentFromServer = fulfillmentMoment;

            // Server may return a different time than the requested one.
            fulfillmentMomentFromServer = cart.fulfillmentDateTime
                ? moment.parseZone(cart.fulfillmentDateTime)
                : null;

            setCart(cart);
            deferred.resolve(cart);
            cartStatusChannel.configuredCart(fulfillmentMomentFromServer ? fulfillmentMomentFromServer.format() : null);
        }, function(response) {
            if (errorHandler) {
                errorHandler(response);
            }

            deferred.reject(response);
        });

        return deferred.promise;
    }

    /**
      * _getTrimmedItem(): takes an item and its modifierGroups and trims the hierarchy
      *   -> Only leaves the fields and objects necessary for adding to cart & placing an order
      *   -> E.g. images, unselected options, flags, etc. will be removed.
      */
    function _getTrimmedItem(item) {
        var trimmedItem = {
            name: item.displayName || item.name,
            price: item.price,
            groupIdString: item.menuGroupGuidString || item.groupIdString,
            groupGuid: item.groupGuid,
            idString: item.guidString || item.idString,
            guid: item.guid,
            quantity: item.quantity,
            specialRequest: item.specialRequest,
            modifierGroups: getTrimmedModifiers(item)
        };

        if (item.hasOwnProperty('uniqueIdString')) {
            trimmedItem.uniqueIdString = item.uniqueIdString;
        }

        return trimmedItem;
    }

    /**
      * getTrimmedModifiers(): trims a modifier hierarchy to only selected options
      *   -> Empty modifierGroups will be omitted
      */
    function getTrimmedModifiers(node) {
        var trimmedModifierGroups = [],
            trimmedOptions = [];

        if (node.hasOwnProperty('modifierGroups')) {
            for (const modifierGroup of node.modifierGroups) {
                trimmedOptions = getTrimmedModifiers(modifierGroup);

                // If after removing all selected options the group has no options, remove it.
                if (trimmedOptions.length > 0) {
                    trimmedModifierGroups.push({
                        idString: modifierGroup.guidString || modifierGroup.idString,
                        guid: modifierGroup.guid,
                        name: modifierGroup.name,
                        options: trimmedOptions
                    });
                }
            }

            return trimmedModifierGroups;
        } else if (node.hasOwnProperty('modifiers') || node.hasOwnProperty('options')) {
            const options = node.modifiers || node.options;
            for (const option of options) {
                if (option.isSelected || option.selected) {
                    trimmedModifierGroups = getTrimmedModifiers(option);

                    trimmedOptions.push({
                        idString: option.itemReferenceGuidString || option.idString,
                        itemGroupIdString: option.itemGroupGuidString || option.itemGroupGuid,
                        itemGroupGuid: option.itemGroupGuidString || option.itemGroupGuid,
                        guid: option.guid,
                        name: option.displayName || option.name,
                        selected: true,
                        quantity: option.quantity,
                        modifierGroups: trimmedModifierGroups
                    });
                }
            }

            return trimmedOptions;
        }

        return [];
    }

    /**
     * Common error handler for any cart manipulation request (add/edit/delete item,
     * set/remove promo code, configure/update cart).  Will handle out of stock messages.
     *
     * `skipCallout: true` should be passed to the $http calls that use this error handler
     * and instead pass the desired `skipCallout` value to this function since
     * this handler supersedes the httpRequestInterceptor that would normally handle
     * errors.
     *
     */
    function modifyCartErrorHandler({ errResp, skipCallout } = {}) {
        const { errorMessage, error } = (errResp || {}).data || {};

        // `skipCallout: true` should only be set explicitly for enlightened calls
        if (!skipCallout) {
            if (OUT_OF_STOCK_ERROR === error) {
                const res = blockingCalloutService.error(errorMessage, error);

                // Need to ensure the cart is refreshed in the UI.
                clearCart();
                getCart().then(() => {
                    // This prevents an unnecessary web call when updateCart() is called
                    // as a result of the below changedCart() cart call.
                    isCartUpdated = true;

                    cartStatusChannel.changedCart();
                });

                return $q.reject(res);
            }

            // `skipCallout: true` should have been passed to $http so we need to show
            // the callout ourselves.
            calloutService.error(errorMessage, error);
        }

        return $q.reject(errResp);
    }

    /**
      * addCartItem(): adds an item object, or edits an existing one
      *  -> In addition to the change in the front end model, we will also update the server-side shopping cart
      *     either by creating a new cart item or updating an existing one.
      */
    function addCartItem(item, skipCallout) {
        const itemCopy = angular.copy(item);
        const cartItem = _getTrimmedItem(itemCopy);
        let cart = cartService.data.cart;

        if (!cart) {
            setCart({});
            cart = cartService.data.cart;
        }

        if (!cart.items) {
            cart.items = [];
            setCart(cart);
            cart = cartService.data.cart;
        }

        let addItemPromise;
        let errorHandler;

        const modifiers = [];

        if (cartItem.specialRequest) {
            modifiers.push({
                selectionType: 'SPECIAL_REQUEST',
                displayName: cartItem.specialRequest
            });
        }

        modifiers.push(..._buildModifierSelections(cartItem, cartItem.quantity));

        const requestData = {
            itemGroup: {
                guid: cartItem.groupGuid
            },
            item: {
                guid: cartItem.guid
            },
            quantity: cartItem.quantity,
            modifiers
        };

        let httpArgs = {
            url: _getCartItemUrl(_data.cartGuid, cartItem.uniqueIdString),
            method: 'POST',
            dataType: 'JSON',
            // We need our own error handler below.
            skipCallout: true,
            headers: {
                'Content-Type': 'application/json'
            },
            data: requestData
        };

        if (cartItem.uniqueIdString) {
            httpArgs.method = 'PUT';
            requestData.externalId = cartItem.uniqueIdString;
        }

        addItemPromise = $http(httpArgs);

        errorHandler = errResp => modifyCartErrorHandler({ errResp, skipCallout });

        return addItemPromise.then(function(response) {
            _setUpdatedCart(response.data);
        }, errorHandler);
    }

    // Following OOShadowCartService.buildModifierSelections() in toastweb.
    function _buildModifierSelections(trimmedItem, quantity) {
        const modifiers = [];

        _.each(trimmedItem.modifierGroups, modifierGroup => {
            _.each(modifierGroup.options, option => {
                const itemGroup = option.itemGroupGuid ? {
                    guid: option.itemGroupGuid
                } : null;

                const modifier = {
                    optionGroup: {
                        guid: modifierGroup.guid,
                    },
                    item: {
                        guid: option.guid
                    },
                    itemGroup,
                    quantity,
                    modifiers: _buildModifierSelections(option, quantity)
                };

                modifiers.push(modifier);
            });
        });

        return modifiers;
    }

    /**
      * removeCartItem(): removes the given item from the cart using its unique id
      *  -> Removes the cart item from the server-side cart as well.
      */
    function removeCartItem(item) {
        let removeItemPromise;
        let errorHandler;

        removeItemPromise = $http({
            url: _getCartItemUrl(_data.cartGuid, item.uniqueIdString),
            method: 'DELETE',
            skipCallout: true
        });
        errorHandler = errResp => modifyCartErrorHandler({ errResp });

        return removeItemPromise.then(function(response) {
            _setUpdatedCart(response.data);
        }, errorHandler);
    }

    /**
      * Updates the cart on the server
      */
    function updateCart({
        deliveryInfo,
        paymentMethod,
        skipCallout,
        tipAmount
    } = {}) {
        if (isCartUpdated) {
            isCartUpdated = false;
            const dfd = $q.defer();
            dfd.resolve();
            return dfd.promise;
        }

        var payload = {};
        let cart = cartService.data.cart || {};

        // Arguments take precedence over cart fields

        payload.deliveryInfo = deliveryInfo || _.get(cart, 'order.deliveryInfo');

        if (tipAmount || cart.tipAmount) {
            var payloadTip = tipAmount || cart.tipAmount;
            if (typeof (payloadTip) === 'number') {
                payload.tip = payloadTip;
            }
        }

        if (paymentMethod || cart.paymentMethod) {
            payload.paymentMethod = paymentMethod || cart.paymentMethod;
        }

        let updateCartPromise;
        let errorHandler;

        updateCartPromise = _createCartIfAbsent().then(() => $http({
            url: `${getCartUrl()}/config`,
            method: 'PATCH',
            dataType: 'JSON',
            headers: {
                'Content-Type': 'application/json'
            },
            data: payload,
            skipCallout: true
        }));

        errorHandler = errResp => modifyCartErrorHandler({ errResp, skipCallout });

        return updateCartPromise.then(function(response) {
            cart = response.data;
            setCart(cart);
            return cart;
        }, errorHandler);
    }

    function clearCart() {
        let cart = cartService.data.cart;
        completedCarts.push(angular.copy(cart));
        cart = {};
        setCart(cart);
    }

    function getPreviousCart() {
        return completedCarts.length ? completedCarts[completedCarts.length - 1] : null;
    }

    /**
     * Sets the current cart, transforming as necessary. Intended to only be called when
     * a cart response is received from the server. Ideally would be private within
     * cartService, but other javascript modules also make server calls that return
     * a cart.
     *
     * @param {*} cart a cart object from the server
     */
    function setCart(cart) {
        if (cart) {
            _translateToLegacyCart(cart);

            let prepTimeChanged = {
                [ORDER_TYPES.TAKE_OUT]: false,
                [ORDER_TYPES.DELIVERY]: false
            };

            if (typeof cart.takeoutPrepTime === 'number' && cart.takeoutPrepTime >= 0) {
                prepTimeChanged[ORDER_TYPES.TAKE_OUT] = restaurantHoursService.setPrepTime(cart.takeoutPrepTime, ORDER_TYPES.TAKE_OUT);
            }

            if (typeof cart.deliveryPrepTime === 'number' && cart.deliveryPrepTime >= 0) {
                prepTimeChanged[ORDER_TYPES.DELIVERY] = restaurantHoursService.setPrepTime(cart.deliveryPrepTime, ORDER_TYPES.DELIVERY);
            }

            _notifyChanges(prepTimeChanged[cart.diningOptionBehavior], cart);

            if (cart.guid) {
                _setCartGuid(cart.restaurantGuid, cart.guid);
            }
        }

        cartService.data.cart = cart;
        cartService.data.cartSize = _calculateCartSize(cart);
    }

    /**
     * Modifies `cart` in-place.
     * @param {*} cart a G2 cart from toast-oo
     * @returns {void}
     */
    function _translateToLegacyCart(cart) {
        if (!_.hasIn(cart, 'order.checks[0]')) {
            return;
        }

        cart.check = _translateToLegacyCheck(cart.order.checks[0]);
        cart.items = cart.check.items;
    }

    /**
     *
     * @param {*} check a G2 check from toast-oo
     * @returns {object} a legacy check object
     */
    function _translateToLegacyCheck(check) {
        const items = _.map(check.selections, g2Selection => {
            const item = _translateToLegacySelection(g2Selection);
            // We only want to do this on top level items.
            _fillLegacyModifierGroupNamesAsync(item);
            return item;
        });
        return {
            tax: check.taxAmount,
            tip: check.payments[0].tipAmount,
            subtotal: _calculateSubtotal(items),
            totalAmount: check.totalAmount,
            appliedPromoCode: _translateToLegacyAppliedPromoCode(check),
            promoCodeDiscount: _translateToLegacyPromoCodeDiscount(check),
            loyaltyDiscountAmount: _getLoyaltyDiscountAmount(check),
            loyaltyDiscountGuid: _getLoyaltyDiscountGuid(check),
            deliveryFee: _translateToLegacyDeliveryFee(check),
            svcCharges: _translateToLegacyServiceCharges(check),
            items: items
        };
    }

    /**
     *
     * @param {*} g2Selection a G2 selection from toast-oo, may be an item or a modifier
     * @return {object} a legacy item object
     */
    // This is a lot like OrderService.mapToMenuItem() in toast-customermgmt.
    function _translateToLegacySelection(g2Selection) {
        const item = {
            g2Selection: _.cloneDeep(g2Selection),
            uniqueIdString: g2Selection.externalId,
            name: g2Selection.displayName,
            price: g2Selection.preDiscountPrice,
            quantity: g2Selection.quantity,
            guid: _.get(g2Selection, 'item.guid'),
            groupGuid: _.get(g2Selection, 'itemGroup.guid'),
            idString: _.get(g2Selection, 'item.guid'),
            groupIdString: _.get(g2Selection, 'itemGroup.guid'),
            optionGroupGuid: _.get(g2Selection, 'optionGroup.guid'),
            modifierGroups: _translateToLegacyModifierGroups(g2Selection.modifiers)
        };

        const specialRequestModifier = _.find(g2Selection.modifiers, {
            selectionType: 'SPECIAL_REQUEST'
        });

        if (specialRequestModifier) {
            item.specialRequest = specialRequestModifier.displayName;
        }

        return item;
    }

    /**
     * Asynchronously fill in all the modifier group names for the given item.
     * @param {*} item a legacy G1 item that was translated from a G2 selection
     * @returns {void}
     */
    function _fillLegacyModifierGroupNamesAsync(item) {
        itemDetailsService.getModifierGroupsByGuidAsync(item.groupGuid, item.guid)
            .then(modifierGroups => _fillLegacyModifierGroupNamesHandler(item, modifierGroups));
    }

    function _fillLegacyModifierGroupNamesHandler(item, modifierGroupsFromServer) {
        const nameMap = _getModifierGroupNameMap(modifierGroupsFromServer);
        _fillLegacyModifierGroupNamesFromNameMap(nameMap, item.modifierGroups || []);
    }

    /**
     * Accumulates a flat guid/name map for each modifier group recursively.
     *
     * @param {Array} modifierGroups
     * @returns {Map<string, string>} maps from modifier group guids to the group's name
     */
    function _getModifierGroupNameMap(modifierGroups) {
        return _.reduce(modifierGroups, (result, modifierGroup) => {
            result[modifierGroup.guid] = modifierGroup.name;

            return Object.assign(result, ..._.map(modifierGroup.options || [], option =>
                _getModifierGroupNameMap(option.modifierGroups || []))
            );
        }, {});
    }

    /**
     * Recursively sets each modifier group name from the given `nameMap`.
     *
     * @param {Map<string, string>} nameMap a flat guid/name map
     * @param {Array} modifierGroups
     */
    function _fillLegacyModifierGroupNamesFromNameMap(nameMap, modifierGroups) {
        _.forEach(modifierGroups, modifierGroup => {
            modifierGroup.name = nameMap[modifierGroup.guid];

            _.forEach(modifierGroup.options || [], option => {
                _fillLegacyModifierGroupNamesFromNameMap(nameMap, option.modifierGroups || []);
            });
        });
    }

    /**
     * G2 modifiers are flattened and not nested within their ModifierGroup. We need to
     * inflate them to match the legacy structure.
     *
     * @param {Array} g2Modifiers array of G2 modifiers from a toast-oo cart selection
     * @returns {Array}
     */
    function _translateToLegacyModifierGroups(g2Modifiers) {
        // Need to maintain order they came in from the cart.
        const optionGroupMap = {};
        const optionGroupGuids = [];

        _.forEach(g2Modifiers, g2Modifier => {
            if (g2Modifier.selectionType === 'SPECIAL_REQUEST') {
                return;
            }

            if (!optionGroupMap[g2Modifier.optionGroup.guid]) {
                optionGroupGuids.push(g2Modifier.optionGroup.guid);
                optionGroupMap[g2Modifier.optionGroup.guid] = {
                    guid: g2Modifier.optionGroup.guid,
                    idString: g2Modifier.optionGroup.guid,
                    // We need to fill in the real name later via _fillLegacyModifierGroupNames().
                    // name: ...,
                    options: []
                };
            }

            const item = _translateToLegacySelection(g2Modifier);
            item.selected = true;

            // G1 never returned quantity for modifiers, it always equals the quantity of the
            // base item. We will want this when we need to solve the donut problem.
            Reflect.deleteProperty(item, 'quantity');

            optionGroupMap[g2Modifier.optionGroup.guid].options.push(item);
        });

        return _.map(optionGroupGuids, guid => optionGroupMap[guid]);
    }

    /**
     *
     * @param {object} check the check from toast-oo
     * @return {string} the applied promo code
     */
    function _translateToLegacyAppliedPromoCode(check) {
        let appliedPromoCode = _.get(check, 'appliedDiscounts[0].appliedPromoCode');

        if (!appliedPromoCode) {
            appliedPromoCode = _(check.selections || [])
                .filter(selection => _.hasIn(selection, 'appliedDiscounts[0].appliedPromoCode'))
                .map('appliedDiscounts[0].appliedPromoCode')
                .first();
        }

        return appliedPromoCode || '';
    }

    /**
     *
     * @param {object} check the check from toast-oo
     * @return {Number} the promo code discount
     */
    function _translateToLegacyPromoCodeDiscount(check) {
        const checkAppliedDiscountAmount = _(check.appliedDiscounts || [])
            .filter(appliedDiscount => appliedDiscount.appliedPromoCode && appliedDiscount.nonTaxDiscountAmount)
            .map('nonTaxDiscountAmount')
            .sum();

        const selectionsAppliedDiscountAmount = _(check.selections || [])
            .flatMap('appliedDiscounts')
            .map('nonTaxDiscountAmount')
            .sum();

        return checkAppliedDiscountAmount + selectionsAppliedDiscountAmount;
    }

    /**
     *
     * @param {object} check the check from toast-oo
     * @return {string} the loyalty discount amount
     */
    function _getLoyaltyDiscountAmount(check) {
        // Toast-loyalty only has check level discounts;
        return _(check.appliedDiscounts || [])
            .filter(appliedDiscount => appliedDiscount.loyaltyDetails && appliedDiscount.nonTaxDiscountAmount)
            .map('nonTaxDiscountAmount')
            .first();
    }

    /**
     *
     * @param {object} check the check from toast-oo
     * @return {string} the loyalty discount guid
     */
    function _getLoyaltyDiscountGuid(check) {
        // Toast-loyalty only has check level discounts;
        return _(check.appliedDiscounts || [])
            .filter(appliedDiscount => appliedDiscount.loyaltyDetails && appliedDiscount.nonTaxDiscountAmount)
            .map('discount.guid')
            .first();
    }

    /**
     *
     * @param {object} check the check from toast-oo
     * @return {Number} the delivery fee
     */
    function _translateToLegacyDeliveryFee(check) {
        return _(check.appliedServiceCharges || [])
            .filter(appliedServiceCharge =>
                appliedServiceCharge.delivery && appliedServiceCharge.chargeAmount)
            .map('chargeAmount')
            .sum();
    }

    function _translateToLegacyServiceCharges(check) {
        return _(check.appliedServiceCharges || [])
            .filter(appliedServiceCharge =>
                !appliedServiceCharge.delivery)
            .map(appliedServiceCharge => Object.assign({}, {
                name: appliedServiceCharge.name,
                amount: appliedServiceCharge.chargeAmount,
                isTaxable: appliedServiceCharge.taxable
            }))
            .value();
    }

    function _calculateSubtotal(items) {
        return _(items || [])
            .map('price')
            .sum();
    }

    function _notifyChanges(prepTimeChanged, cart) {
        if(_.has(cart, 'warnings[0].message')) {
            const message = _.get(cart, 'warnings[0].message');
            blockingCalloutService.show(message);
            return;
        }

        if(_shouldNotify(prepTimeChanged, cart)) {
            const message = 'Your order time has been updated due to your order size.';
            calloutService.info(message);
        }
    }

    function _shouldNotify(prepTimeChanged = false, cart) {
        if(!_.has(cartService.data, 'cart.fulfillmentDateTime')) return false;
        const fulfillmentTypeChanged = _fulfillmentTypeChanged(cart);
        const fulfillmentDateTimeChanged = _fulfillmentDateTimeChanged(cart);
        const isASAP = !cart.fulfillmentDateTime;

        if(fulfillmentTypeChanged) return false;
        if(!isASAP && fulfillmentDateTimeChanged) return true;
        if(isASAP && prepTimeChanged) return true;

        return false;
    }

    function _fulfillmentTypeChanged(newCart) {
        return !!newCart.fulfillmentDateTime !== !!cartService.data.cart.fulfillmentDateTime;
    }

    function _fulfillmentDateTimeChanged(newCart) {
        if(!newCart.fulfillmentDateTime && !cartService.data.cart.fulfillmentDateTime) return false;
        if(!newCart.fulfillmentDateTime || !cartService.data.cart.fulfillmentDateTime) return true;

        const requestedFulfillmentMoment = moment.parseZone(newCart.requestedFulfillmentDateTime);
        const previousFulfillmentMoment = moment.parseZone(cartService.data.cart.fulfillmentDateTime);
        const currentFulfillmentMoment = moment.parseZone(newCart.fulfillmentDateTime);

        if(requestedFulfillmentMoment.isSame(currentFulfillmentMoment)) return false;
        return !previousFulfillmentMoment.isSame(currentFulfillmentMoment);
    }

    function _setUpdatedCart(cart) {
        setCart(cart);
        isCartUpdated = true;
    }

    function _calculateCartSize(cart) {
        if(cart && cart.items) {
            return cart.items.reduce((prev, item) => prev + item.quantity, 0);
        }
        return 0;
    }
}
